import { TILT_DOMAIN_HI, TILT_DOMAIN_LO, wrapTurn, type RotationOrientation, type RotationPosture } from '@agavi/blocks';
import { RotateCcw } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { fmtLocale } from './number-format';
import { useDialDrag } from './use-dial-drag';

import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { SegmentedControl } from '@/components/ui/segmented-control';

/** One angle block's accepted range, shaded so a teacher can drive onto it and around it. */
export interface RotationBand {
  id: string;
  /** The block's own name, so several blocks' bands stay tellable apart. */
  name: string;
  from: number;
  to: number;
}

export interface SimAttitude {
  posture: RotationPosture;
  orientation: RotationOrientation;
  /** Where the phone turns about: a corner of it, or its middle. */
  centre: 'pivot' | 'rotate';
  /** Where the phone is when Calibrate is pressed. */
  startDegrees: number;
  /** Where it ends up, and therefore what the block should report. */
  endDegrees: number;
  interrupted: boolean;
}

const SIZE = 200;
const C = SIZE / 2;
/** Leaves room outside the rim for the quarter labels AND a gap under the 270 one, which the
 * readout sits in. At 78 the label ran into the number below it. */
const R = 70;
const BAND_INNER = 55;

/** Capped, because the panel holds up to three channels and this is the one that would happily
 * take a square. Small enough that a map above it and a signal switch below both still fit. */
const DIAL_MAX_PX = 150;

/** The phone, in its own coordinates before it is turned. Long side 76, short side 42. */
const LONG = 76;
const SHORT = 42;

/**
 * Where zero points, which differs by channel and is the one thing about the drawing that does.
 *
 * ROTATION puts zero at the LEFT and counts clockwise: a sweep is a journey round, and zero is
 * the phone lying along the horizontal that every authored answer is measured from.
 *
 * TILT puts LEVEL AT THE TOP and leans either side of it, which is how a protractor is read and
 * how a lean looks. Rotation's convention is not wrong for a tilt so much as unreadable as one —
 * it leaves level pointing sideways and the two leans at the top and bottom of the dial.
 *
 * `pointAt` and `bearingOf` are generated TOGETHER from this, because they are two halves of one
 * fact and shipping them out of step is what once made a dial turn opposite to the hand.
 */
export type DialZero = 'left' | 'top';

type PointAt = (deg: number, radius: number) => { x: number; y: number };

export function pointAtFor(zero: DialZero) {
  return (deg: number, radius: number): { x: number; y: number } => {
    const rad = (deg * Math.PI) / 180;
    return zero === 'top'
      ? { x: C + Math.sin(rad) * radius, y: C - Math.cos(rad) * radius }
      : { x: C - Math.cos(rad) * radius, y: C - Math.sin(rad) * radius };
  };
}

/** The exact inverse of `pointAtFor(zero)`, for `useDialDrag`. */
export function bearingOf(zero: DialZero) {
  return (dx: number, dy: number): number => (zero === 'top'
    ? (Math.atan2(dx, -dy) * 180) / Math.PI
    : (Math.atan2(-dy, -dx) * 180) / Math.PI);
}



/** An annular wedge from `from` to `to`, clockwise on screen as the dial reads. */
function bandPath(pointAt: PointAt, from: number, to: number): string {
  const span = Math.max(0.01, Math.min(360, to - from));
  const large = span > 180 ? 1 : 0;
  const o1 = pointAt(from, R);
  const o2 = pointAt(from + span, R);
  const i2 = pointAt(from + span, BAND_INNER);
  const i1 = pointAt(from, BAND_INNER);
  return `M ${o1.x} ${o1.y} A ${R} ${R} 0 ${large} 1 ${o2.x} ${o2.y} `
    + `L ${i2.x} ${i2.y} A ${BAND_INNER} ${BAND_INNER} 0 ${large} 0 ${i1.x} ${i1.y} Z`;
}

/** The sweep from `from` to `to`, the short way if it is short and the long way if the teacher
 * dragged it there — always clockwise, which is the direction the dial counts. */
function arcPath(pointAt: PointAt, from: number, to: number, radius: number): string {
  const span = ((to - from) % 360 + 360) % 360;
  if (span < 0.5) return '';
  const a = pointAt(from, radius);
  const b = pointAt(to, radius);
  return `M ${a.x} ${a.y} A ${radius} ${radius} 0 ${span > 180 ? 1 : 0} 1 ${b.x} ${b.y}`;
}

/**
 * The simulated phone, drawn as the arrangement it is in, and turned by dragging it.
 *
 * Three switchers, because a teacher's apparatus has three independent facts about it, and only the
 * first changes what the block reports:
 *
 *   FLAT / UPRIGHT      lying on the thing being measured, or stood up facing you. A tilt or a
 *                       sweep, which are different quantities.
 *   PORTRAIT / LANDSCAPE which way round. A picture, since the sweep began measuring from its own
 *                       calibration attitude — before that, portrait read a quarter turn out.
 *   PIVOT / ROTATE      hinged at a corner, or turning about its middle. A picture, unavoidably:
 *                       gravity is a direction, so it sees the axis a phone turns about but never
 *                       where along that axis the hinge sits.
 *
 * That two of the three change nothing is worth showing rather than hiding. A teacher who suspects
 * the way a student holds the phone matters can set all eight arrangements and watch the number
 * stay put, which is a stronger answer than a sentence saying so.
 */
/**
 * What a channel's quantity can do, which is the only thing the two differ by.
 *
 * ONE dial, driven by the block it is standing in for. Rotation integrates a path from a known
 * start, so it has a calibration to place and a stream that can be interrupted, and its domain is
 * a loop that wraps. Tilt reads a pose: gravity is its own reference, every sample is
 * independently the whole answer, and its domain has real ends.
 *
 * Expressed as capabilities rather than a `mode` enum so each line says WHY it differs, and so a
 * third channel declares what it has instead of matching whichever existing name looks closest.
 */
export interface DialChannel {
  /** Degrees the dial can reach. A full turn wraps; anything less clamps. */
  domain: { lo: number; hi: number };
  /** Where zero sits on screen — see `DialZero`. */
  zero: DialZero;
  /** A second handle for the attitude the reading is counted FROM. Rotation only. */
  calibration: boolean;
  /** Whether a broken sample stream is a condition worth arming. Rotation only — a pose has no
   *  history for a gap to cost. */
  interruptible: boolean;
}

export const ROTATION_CHANNEL: DialChannel =
  { domain: { lo: 0, hi: 360 }, zero: 'left', calibration: true, interruptible: true };

/**
 * Tilt: level in the middle, both leans reachable, and nothing to set up first.
 *
 * No calibration, because gravity is its own reference — there is no attitude the reading is
 * counted FROM. Not interruptible, because every sample is independently the whole answer, so a
 * gap in the stream costs it nothing. And the domain has real ends rather than a seam.
 */
export const TILT_CHANNEL: DialChannel =
  { domain: { lo: TILT_DOMAIN_LO, hi: TILT_DOMAIN_HI }, zero: 'top', calibration: false, interruptible: false };

export function SensorRotationView({ bands, attitude, onAttitudeChange, disabled, fillHeight, channel = ROTATION_CHANNEL }: {
  bands: RotationBand[];
  attitude: SimAttitude;
  onAttitudeChange: (next: SimAttitude) => void;
  disabled?: boolean;
  fillHeight?: boolean;
  channel?: DialChannel;
}) {
  const { lo: DLO, hi: DHI } = channel.domain;
  /** This channel's drawing and its hit-testing, generated as the pair they are. */
  const pointAt = pointAtFor(channel.zero);
  /** A full turn is a loop and wraps; a shorter span has real ends and stops at them. */
  const bound = (v: number) => (DHI - DLO >= 360 ? wrapTurn(v) : Math.min(DHI, Math.max(DLO, v)));
  const { t, i18n } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);

  const set = (next: Partial<SimAttitude>) => onAttitudeChange({
    ...attitude, ...next,
    // Wrapped, not clamped: the block grades over [0, 360] and identifies 0 with 360, so a 400
    // degree turn leaves the phone at 40 and 40 is what it reads. Stopping the dial dead at a bound
    // the block has no equivalent of would have been the simulator inventing a rule.
    ...(next.startDegrees === undefined ? {} : { startDegrees: bound(next.startDegrees) }),
    ...(next.endDegrees === undefined ? {} : { endDegrees: bound(next.endDegrees) }),
  });

  // Two handles on one circle; the hook picks whichever the press landed nearer in ANGLE.
  const dial = useDialDrag({
    svgRef, size: SIZE, disabled,
    // The other half of `pointAt`. The hook's own default is Rotation's, and letting Tilt take it
    // is precisely what once made the dial turn opposite to the hand.
    bearing: bearingOf(channel.zero),
    // A channel with no calibration offers one handle, so a press can only ever grab that one.
    handles: (channel.calibration
      ? { start: attitude.startDegrees, end: attitude.endDegrees }
      : { end: attitude.endDegrees }) as Record<'start' | 'end', number>,
    onChange: (held, deg) => set(held === 'start' ? { startDegrees: deg } : { endDegrees: deg }),
  });

  /** Every 15 degrees across whatever the channel reaches, so a half-span dial is not drawn with
   *  a full turn of ticks nobody can get to. */
  const ticks: number[] = [];
  for (let d = DLO; d < DHI - 1e-9 || (DHI - DLO < 360 && d <= DHI + 1e-9); d += 15) ticks.push(Math.round(d));

  const fmt = (deg: number) => fmtLocale(Math.round(deg * 10) / 10, i18n.language);
  const upright = attitude.posture === 'upright';
  const portrait = attitude.orientation === 'portrait';
  /** Along the turn, and across it. Flat is seen EDGE-ON, so its across-dimension is its thickness:
   * portrait shows the short edge to you and landscape the long one, which is the whole difference. */
  const along = portrait ? SHORT : LONG;
  const across = upright ? (portrait ? LONG : SHORT) : 5;
  /** `pivot` puts a corner on the turn point and the body out along zero, which is to the LEFT;
   * `rotate` centres the phone on it. */
  const originX = attitude.centre === 'pivot' ? -along : -along / 2;
  const originY = attitude.centre === 'pivot' ? -across : -across / 2;

  return (
    <div className={`flex min-w-0 items-center gap-3 ${fillHeight ? 'min-h-0 flex-1' : ''}`}>
      <div className={`relative flex min-w-0 flex-col items-center ${fillHeight ? 'h-full' : 'w-1/2'}`}>
        {/* Reset, in the corner, the same gesture and the same corner the map's own Reset uses.
            Puts BOTH ends of the play back to horizontal; the arrangement and the armed
            interruption are the teacher's setup rather than a position, so they stay. */}
        {!disabled && (
          <IconButton
            size='sm'
            onClick={() => set({ startDegrees: 0, endDegrees: 0 })}
            aria-label={t('task.sensorSim.resetRotation')}
            title={t('task.sensorSim.resetRotation')}
            className='absolute left-0 top-0 z-10 bg-ws-surface/90'
          >
            <RotateCcw aria-hidden='true' className='h-3.5 w-3.5' />
          </IconButton>
        )}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role='presentation'
          style={fillHeight ? undefined : { maxWidth: DIAL_MAX_PX }}
          className={`mx-auto ${fillHeight ? 'h-full w-auto' : 'w-full'} touch-none select-none ${disabled ? 'opacity-40' : 'cursor-grab'}`}
          {...dial}
        >
          {bands.map(b => (
            <path key={b.id} data-angle-band={b.name} d={bandPath(pointAt, b.from, b.to)}
              className='fill-ws-primary/20 dark:fill-ws-text-link/20' />
          ))}
          <circle cx={C} cy={C} r={R} className='fill-none stroke-ws-border' strokeWidth={1.5} />
          {/* Every 15 degrees, so a teacher can read an angle off the dial rather than only off the
              number below it. The quarters run the full radius and carry their value; the rest are
              short marks on the rim. */}
          {ticks.map(deg => {
            const quarter = deg % 90 === 0;
            const a = pointAt(deg, quarter ? 0 : R - 7);
            const b = pointAt(deg, R);
            return (
              <line key={deg} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                className={quarter ? 'stroke-ws-border' : 'stroke-ws-muted/50'}
                strokeWidth={quarter ? 1.5 : 1.25} />
            );
          })}
          {ticks.filter(d => d % 90 === 0).map(deg => {
            // Far enough out to clear the calibration handle, which sits ON the rim and is eight
            // across — at R+12 the C and the 0 sat on top of each other, and 0 is where the
            // calibration starts, so the collision was the first thing on screen.
            const at = pointAt(deg, R + 17);
            return (
              <text key={deg} x={at.x} y={at.y} textAnchor='middle' dominantBaseline='central'
                className='fill-ws-muted text-[11px] tabular-nums'>{deg}</text>
            );
          })}

          {/*
            The phone's own line, carried out to the rim.

            The phone is small and sits near the middle, so reading its angle off the ticks meant
            projecting it by eye — and in `rotate` mode, where it straddles the turn point, there is
            not even an end to sight along. Dashed rather than solid so it reads as a sightline
            rather than as another part of the apparatus, and drawn UNDER the phone so the phone
            stays the thing you grab.
          */}
          {channel.calibration && (<>
          {/* The sweep the play will perform, start to end, drawn on the band's own ring. */}
          <path d={arcPath(pointAt, attitude.startDegrees, attitude.endDegrees, (R + BAND_INNER) / 2)}
            className='fill-none stroke-ws-primary/35 dark:stroke-ws-text-link/35' strokeWidth={6} strokeLinecap='round' />
          {/* Where Calibrate will be taken. Hollow, because nothing is measured there — it is the
              anchor the reading is counted from. */}
          <line
            x1={C} y1={C} x2={pointAt(attitude.startDegrees, R).x} y2={pointAt(attitude.startDegrees, R).y}
            strokeDasharray='2 4' strokeWidth={1.5} className='stroke-ws-muted'
          />
          <circle cx={pointAt(attitude.startDegrees, R).x} cy={pointAt(attitude.startDegrees, R).y} r={8}
            className='fill-ws-surface stroke-ws-muted' strokeWidth={2} />
          {/* Named on the dial, because two handles on one circle are otherwise told apart only by
              which is hollow — and the phone needs no label to say it is the phone. */}
          <text x={pointAt(attitude.startDegrees, R).x} y={pointAt(attitude.startDegrees, R).y}
            textAnchor='middle' dominantBaseline='central'
            className='fill-ws-muted text-[9px] font-semibold'>C</text>
          </>)}
          {/*
            The phone's own line, carried out to the rim.

            The phone is small and sits near the middle, so reading its angle off the ticks meant
            projecting it by eye — and in `rotate` mode, where it straddles the turn point, there is
            not even an end to sight along. Dashed rather than solid so it reads as a sightline
            rather than as another part of the apparatus, and drawn UNDER the phone so the phone
            stays the thing you grab.
          */}
          <line
            x1={C} y1={C} x2={pointAt(attitude.endDegrees, R).x} y2={pointAt(attitude.endDegrees, R).y}
            strokeDasharray='3 3' strokeWidth={1.5}
            className='stroke-ws-primary/70 dark:stroke-ws-text-link/70'
          />
          <g transform={`translate(${C} ${C}) rotate(${attitude.endDegrees})`}>
            {upright ? (
              <>
                <rect x={originX} y={originY} width={along} height={across} rx={4}
                  className='fill-ws-surface stroke-ws-primary dark:stroke-ws-text-link' strokeWidth={2.5} />
                {/* The screen, so "facing you" reads as facing you. */}
                <rect x={originX + 5} y={originY + 5} width={along - 10} height={across - 10} rx={2}
                  className='fill-ws-primary/25 dark:fill-ws-text-link/25' />
              </>
            ) : (
              <rect x={originX} y={originY} width={along} height={across} rx={2.5}
                className='fill-ws-surface stroke-ws-primary dark:stroke-ws-text-link' strokeWidth={2.5} />
            )}
          </g>
          {/* The turn point itself, drawn last so the phone never covers it. */}
          <circle cx={C} cy={C} r={4} className='fill-ws-primary dark:fill-ws-text-link' />
        </svg>

        {/*
          One slider per end of the play, which is also the keyboard route. A drag-only control
          would be a new unnamed, unreachable widget straight after the accessible-names pass — and
          with two handles on one circle, keys are the only way to place them precisely.
        */}
        <div className='flex items-center justify-center gap-2'>
          {([
            ...(channel.calibration
              ? [['start', attitude.startDegrees, t('task.sensorSim.rotationStartLabel'), 'text-ws-muted'] as const]
              : []),
            ['end', attitude.endDegrees, t('task.sensorSim.rotationEndLabel'), 'text-ws-text'] as const,
          ]).map(([which, value, label, tone]) => (
            <div
              key={which}
              role='slider'
              tabIndex={disabled ? -1 : 0}
              aria-label={label}
              aria-valuemin={DLO}
              aria-valuemax={DHI}
              aria-valuenow={Math.round(value)}
              aria-valuetext={t('task.sensorSim.rotationValue', { degrees: fmt(value) })}
              aria-disabled={disabled || undefined}
              onKeyDown={e => {
                if (disabled) return;
                const to = (d: number) => {
                  e.preventDefault();
                  set(which === 'start' ? { startDegrees: d } : { endDegrees: d });
                };
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp') to(value + (e.shiftKey ? 15 : 1));
                else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') to(value - (e.shiftKey ? 15 : 1));
                else if (e.key === 'Home') to(0);
                else if (e.key === 'End') to(DHI);
              }}
              className={`flex items-center gap-1 rounded-md px-1.5 text-center font-mono text-base font-semibold tabular-nums ${tone}`}
            >
              {which === 'start' && (
                // The same C as the dial's marker, so the number and the handle are one thing.
                // `aria-hidden`: the slider is already named "Calibrate at".
                <span aria-hidden='true'
                  className='flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-ws-muted text-[10px] leading-none'
                >C</span>
              )}
              {fmt(value)}&#176;
            </div>
          ))}
        </div>
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-2'>
        <SegmentedControl
          stretch disabled={disabled}
          aria-label={t('task.sensorSim.postureLabel')}
          value={attitude.posture}
          onChange={v => set({ posture: v as RotationPosture })}
          options={[
            { id: 'flat', label: t('task.sensorSim.postureFlat') },
            { id: 'upright', label: t('task.sensorSim.postureUpright') },
          ]}
        />
        <SegmentedControl
          stretch disabled={disabled}
          aria-label={t('task.sensorSim.orientationLabel')}
          value={attitude.orientation}
          onChange={v => set({ orientation: v as RotationOrientation })}
          options={[
            { id: 'portrait', label: t('task.sensorSim.orientationPortrait') },
            { id: 'landscape', label: t('task.sensorSim.orientationLandscape') },
          ]}
        />
        <SegmentedControl
          stretch disabled={disabled}
          aria-label={t('task.sensorSim.centreLabel')}
          value={attitude.centre}
          onChange={v => set({ centre: v as 'pivot' | 'rotate' })}
          options={[
            { id: 'pivot', label: t('task.sensorSim.centrePivot') },
            { id: 'rotate', label: t('task.sensorSim.centreRotate') },
          ]}
        />

        {channel.interruptible && (<>
        {/* A held condition rather than a moment: the tracker's answer to a broken stream is to
            discard the turn count and refuse to re-latch, so a one-shot button made the fault
            depend on when it was pressed. Armed, the same setup fails the same way twice. */}
        <CheckboxRow
          checked={attitude.interrupted}
          disabled={disabled}
          onToggle={() => set({ interrupted: !attitude.interrupted })}
          label={t('task.sensorSim.interrupt')}
        />
        </>)}
      </div>
    </div>
  );
}
